import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  Database,
  FileText,
  GitCompare,
  KeyRound,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  User,
  X,
  XCircle,
} from 'lucide-react';

import { auditAPI } from '../services/api';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'actor', label: 'Actor', icon: User },
  { id: 'resource', label: 'Resource', icon: Database },
  { id: 'evidence', label: 'Evidence', icon: FileText },
  { id: 'authorization', label: 'Authorization', icon: ShieldAlert },
  { id: 'change', label: 'Before / After', icon: GitCompare },
  { id: 'technical', label: 'Technical', icon: KeyRound },
  { id: 'raw', label: 'Raw snapshot', icon: Database },
];

const HIGH_RISK_FIELD_RULES = [
  { keys: ['aadhaar', 'aadhar'], risk: 'critical' },
  { keys: ['pan'], risk: 'critical' },
  { keys: ['passport'], risk: 'critical' },
  { keys: ['bank', 'account', 'ifsc'], risk: 'critical' },
  { keys: ['salary', 'income', 'tax'], risk: 'high' },
  { keys: ['dob', 'date_of_birth', 'birth'], risk: 'high' },
  { keys: ['address', 'postal', 'pincode', 'pin_code'], risk: 'high' },
  { keys: ['phone', 'mobile'], risk: 'medium' },
  { keys: ['email'], risk: 'medium' },
];

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function valueOrDash(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatRelative(value) {
  if (!value) return '—';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return formatDate(value);
  const diffMin = Math.round((Date.now() - timestamp) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.round(diffHr / 24)}d ago`;
}

function normalizeFieldName(value) {
  return String(value || '').trim().toLowerCase().replaceAll(' ', '_');
}

function fieldLabel(field) {
  return String(field || '').replaceAll('_', ' ').replaceAll('.', ' › ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseMaybeJson(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function safeJson(value) {
  if (value === undefined || value === null || value === '') return {};
  if (typeof value === 'object') return value;
  return parseMaybeJson(value);
}

function formatJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function toArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item || '').trim()).filter(Boolean);
    } catch {
      // fallback below
    }
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
}

function unique(values) {
  return Array.from(new Set(toArray(values)));
}

function getSnapshotSource(data, eventData = {}) {
  return data?.snapshot?.source || data?.audit_snapshot?.source || eventData?.evidence_source || (eventData?.snapshot_available ? 'audit_db_snapshot' : 'legacy_raw_proto');
}

function isSnapshotAvailable(data, eventData = {}) {
  return data?.snapshot?.available === true || data?.audit_snapshot?.available === true || eventData?.snapshot_available === true || getSnapshotSource(data, eventData) === 'audit_db_snapshot';
}

function inferCategory(event) {
  const category = String(event?.event_category || '').toLowerCase();
  if (category) return category;
  const eventType = String(event?.event_type || '').toLowerCase();
  const action = String(event?.event_action || event?.action || '').toLowerCase();
  const resourceType = String(event?.resource_type || '').toLowerCase();
  if (eventType.startsWith('auth.') || action.includes('login') || action.includes('otp') || action.includes('logout')) return 'auth';
  if (eventType.startsWith('rbac.') || resourceType.includes('group') || resourceType.includes('permission') || resourceType.includes('admin_user')) return 'rbac';
  if (eventType.startsWith('pii.') || eventType.includes('pii') || resourceType.includes('pii')) return 'pii';
  if (eventType.startsWith('document.') || eventType.includes('document') || eventType.includes('view_session')) return 'document';
  if (eventType.startsWith('ropa.') || resourceType.includes('ropa')) return 'ropa';
  if (eventType.includes('revocation') || resourceType.includes('revocation')) return 'revocation';
  if (eventType.startsWith('microsoft.') || eventType.includes('entra') || eventType.includes('azure_ad')) return 'microsoft';
  if (eventType.includes('suspicious') || eventType.includes('security')) return 'security';
  return 'system';
}

function getOutcome(event) {
  return String(event?.outcome_status || event?.outcome?.status || 'unknown').toLowerCase();
}

function getEventTitle(event) {
  const eventType = String(event?.event_type || '').toLowerCase();
  const action = String(event?.event_action || event?.action || '').toLowerCase();
  const resourceType = String(event?.resource_type || '').toLowerCase();

  if (eventType === 'pii.record.view_token_generated' || action === 'view_token_generated' && resourceType.includes('pii')) {
    return 'PII viewed by admin';
  }

  if (eventType === 'pii.record.view_token_used' || action === 'view_token_used' && resourceType.includes('pii')) {
    return 'PII view opened';
  }

  if (eventType === 'document.view_token_generated' || action === 'view_token_generated' && resourceType.includes('document')) {
    return 'Document viewed by admin';
  }

  if (eventType === 'document.view_token_used' || action === 'view_token_used' && resourceType.includes('document')) {
    return 'Document view opened';
  }

  if (eventType === 'view_session_event' || eventType.includes('view_session')) {
    return 'Document viewed by admin';
  }

  return String(event?.event_type || 'Audit event').replaceAll('_', ' ').replaceAll('.', ' › ');
}

function getResourceLabel(event, resourceSnapshot = {}) {
  return event?.resource_display_name || resourceSnapshot.display_name || resourceSnapshot.name || event?.resource_id || event?.pii_record_uuid || event?.document_id || event?.consent_id || event?.view_session_id || '—';
}

function getActorLabel(event, actorSnapshot = {}) {
  return actorSnapshot.display_name || event?.actor_display_name || event?.admin_full_name || event?.admin_email || event?.admin_user_id || event?.actor_user_id || 'System / Unknown';
}

function getFieldRisk(fieldName, explicitSensitivity = '') {
  const sensitivity = String(explicitSensitivity || '').toUpperCase();
  if (sensitivity === 'CRITICAL') return { risk: 'critical' };
  if (sensitivity === 'HIGH') return { risk: 'high' };
  if (sensitivity === 'MEDIUM') return { risk: 'medium' };
  const normalized = normalizeFieldName(fieldName);
  return HIGH_RISK_FIELD_RULES.find((rule) => rule.keys.some((key) => normalized.includes(key))) || { risk: 'low' };
}

function formatFieldList(fields, max = 3) {
  const safe = unique(fields);
  if (!safe.length) return '';
  const visible = safe.slice(0, max).join(', ');
  const remaining = safe.length - max;
  return remaining > 0 ? `${visible} +${remaining} more` : visible;
}

function fieldMatches(fieldName, candidate) {
  const left = normalizeFieldName(fieldName);
  const right = normalizeFieldName(candidate);
  return left === right || left.endsWith(`.${right}`) || right.endsWith(`.${left}`);
}

function buildFieldAccess(data) {
  const auditSnapshot = data.audit_snapshot || {};
  const evidenceSnapshot = auditSnapshot.evidence_snapshot || data.evidence_snapshot || {};
  const nestedGovernance = evidenceSnapshot.access_governance || evidenceSnapshot.governance || {};
  const direct = data.field_access || evidenceSnapshot.field_access || nestedGovernance.field_access || {};
  const governance =
    data.access_governance ||
    nestedGovernance ||
    evidenceSnapshot ||
    parseMaybeJson(data.event?.access_reason) ||
    parseMaybeJson(data.event?.reason) ||
    {};

  if (direct.available || direct.requested_fields || direct.returned_fields || direct.masked_fields || direct.denied_fields) {
    return {
      ...direct,
      available: direct.available !== false,
      requested_fields: unique(direct.requested_fields || governance.requested_fields || []),
      returned_fields: unique(direct.returned_fields || governance.returned_fields || []),
      masked_fields: unique(direct.masked_fields || governance.masked_fields || []),
      denied_fields: unique(direct.denied_fields || governance.denied_fields || []),
      view_mode: direct.view_mode || governance.view_mode,
      purpose_code: direct.purpose_code || governance.purpose_code,
      access_reason: direct.access_reason || governance.access_reason,
      justification: direct.justification || governance.justification,
      policy: direct.policy || governance.policy || {},
    };
  }

  if (governance.access_type === 'secure_pii_view' || governance.requested_fields || governance.returned_fields) {
    return {
      available: true,
      requested_fields: unique(governance.requested_fields || []),
      returned_fields: unique(governance.returned_fields || []),
      masked_fields: unique(governance.masked_fields || []),
      denied_fields: unique(governance.denied_fields || []),
      denial_reasons: governance.denial_reasons || {},
      policy: governance.policy || {},
      view_mode: governance.view_mode || '',
      purpose_code: governance.purpose_code || '',
      access_reason: governance.access_reason || '',
      justification: governance.justification || '',
    };
  }

  return { available: false, requested_fields: [], returned_fields: [], masked_fields: [], denied_fields: [], denial_reasons: {}, policy: {} };
}

function buildAccessGovernance(data) {
  const auditSnapshot = data.audit_snapshot || {};
  const evidenceSnapshot = auditSnapshot.evidence_snapshot || data.evidence_snapshot || {};
  const nestedGovernance = evidenceSnapshot.access_governance || evidenceSnapshot.governance || {};

  return (
    data.access_governance ||
    nestedGovernance ||
    evidenceSnapshot ||
    parseMaybeJson(data.event?.access_reason) ||
    parseMaybeJson(data.event?.reason) ||
    {}
  );
}

function getFieldDecision(fieldAccess, fieldName) {
  const returned = unique(fieldAccess.returned_fields || []);
  const masked = unique(fieldAccess.masked_fields || []);
  const denied = unique(fieldAccess.denied_fields || []);
  const requested = unique(fieldAccess.requested_fields || []);
  if (returned.some((field) => fieldMatches(fieldName, field))) return { decision: 'returned', label: 'Returned', tone: 'red' };
  if (masked.some((field) => fieldMatches(fieldName, field))) return { decision: 'masked', label: 'Masked', tone: 'amber' };
  if (denied.some((field) => fieldMatches(fieldName, field))) return { decision: 'denied', label: 'Denied', tone: 'slate' };
  if (requested.some((field) => fieldMatches(fieldName, field))) return { decision: 'requested_not_returned', label: 'Requested', tone: 'orange' };
  return { decision: 'not_accessed', label: 'Not accessed', tone: 'slate' };
}

function getEvidenceRisk({ fieldAccess, fieldInventory, eventData }) {
  const returned = unique(fieldAccess.returned_fields || []);
  const masked = unique(fieldAccess.masked_fields || []);
  const denied = unique(fieldAccess.denied_fields || []);
  const viewMode = String(fieldAccess.view_mode || '').toLowerCase();
  const severity = String(eventData?.severity || '').toUpperCase();
  const outcome = getOutcome(eventData);
  const inventoryFields = fieldInventory?.fields || [];

  if (outcome === 'failed' || outcome === 'error') return { level: 'warning', label: 'Failed operation', message: 'This event completed with a failed outcome.' };
  if (outcome === 'denied') return { level: 'warning', label: 'Access denied', message: 'This event was denied by policy or authorization.' };

  const returnedDetails = returned.map((field) => {
    const inventoryMatch = inventoryFields.find((item) => fieldMatches(item.normalized_field_name || item.field_name, field));
    return { field, sensitivity: inventoryMatch?.sensitivity || '', ...getFieldRisk(field, inventoryMatch?.sensitivity) };
  });
  const critical = returnedDetails.filter((item) => item.risk === 'critical');
  const high = returnedDetails.filter((item) => item.risk === 'high');

  if (critical.length) return { level: 'critical', label: 'High-risk PII returned', message: `${critical.length} critical field(s) returned: ${formatFieldList(critical.map((item) => item.field))}.` };
  if (high.length) return { level: 'warning', label: 'Sensitive PII returned', message: `${high.length} sensitive field(s) returned: ${formatFieldList(high.map((item) => item.field))}.` };
  if (denied.length) return { level: 'warning', label: 'Policy denial', message: `${denied.length} requested field(s) were denied by policy.` };
  if (viewMode === 'full' && returned.length) return { level: 'warning', label: 'Full view used', message: `Full view returned ${returned.length} selected field(s).` };
  if (masked.length) return { level: 'ok', label: 'Masked view', message: `${masked.length} field(s) were returned masked.` };
  if (severity === 'CRITICAL') return { level: 'critical', label: 'Critical audit event', message: 'The audit service marked this event as critical.' };
  if (severity === 'WARNING') return { level: 'warning', label: 'Review audit event', message: 'The audit service marked this event for review.' };
  return { level: 'ok', label: 'No access warning', message: 'No high-risk field access was detected from this event payload.' };
}

function toneClasses(tone) {
  if (tone === 'red') return 'bg-red-50 text-red-700 ring-red-100';
  if (tone === 'orange') return 'bg-orange-50 text-orange-700 ring-orange-100';
  if (tone === 'amber') return 'bg-amber-50 text-amber-700 ring-amber-100';
  if (tone === 'emerald') return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (tone === 'blue') return 'bg-blue-50 text-blue-700 ring-blue-100';
  return 'bg-slate-100 text-slate-700 ring-slate-200';
}

function riskClasses(level) {
  if (level === 'critical') return { badge: 'bg-red-50 text-red-700 ring-red-100', card: 'border-red-200 bg-red-50 text-red-800', icon: 'text-red-700' };
  if (level === 'warning') return { badge: 'bg-orange-50 text-orange-700 ring-orange-100', card: 'border-orange-200 bg-orange-50 text-orange-800', icon: 'text-orange-700' };
  if (level === 'info') return { badge: 'bg-amber-50 text-amber-700 ring-amber-100', card: 'border-amber-200 bg-amber-50 text-amber-800', icon: 'text-amber-700' };
  return { badge: 'bg-slate-100 text-slate-700 ring-slate-200', card: 'border-slate-200 bg-slate-50 text-slate-700', icon: 'text-slate-500' };
}

function Badge({ children, tone = 'slate' }) {
  return <span className={cx('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1', toneClasses(tone))}>{children}</span>;
}

function SeverityBadge({ severity }) {
  const normalized = String(severity || 'INFO').toUpperCase();
  const tone = normalized === 'CRITICAL' ? 'red' : normalized === 'WARNING' ? 'orange' : 'slate';
  return <Badge tone={tone}>{normalized}</Badge>;
}

function OutcomeBadge({ status }) {
  const normalized = String(status || 'unknown').toLowerCase();
  const tone = normalized === 'success' ? 'emerald' : ['failed', 'error', 'denied'].includes(normalized) ? 'red' : 'slate';
  return <Badge tone={tone}>{normalized}</Badge>;
}

function SectionCard({ title, icon: Icon, description, children, className = '' }) {
  return (
    <section className={cx('rounded-2xl border border-slate-200 bg-white p-5 shadow-sm', className)}>
      <div className="mb-4 flex items-start gap-3">
        {Icon && <div className="rounded-xl bg-orange-50 p-2 text-orange-600 ring-1 ring-orange-100"><Icon className="h-4 w-4" /></div>}
        <div><h3 className="font-black text-slate-950">{title}</h3>{description && <p className="mt-1 text-sm text-slate-500">{description}</p>}</div>
      </div>
      {children}
    </section>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="grid gap-1 border-b border-slate-100 py-2 last:border-b-0 sm:grid-cols-[190px_1fr]">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={cx('break-words text-sm text-slate-700', mono && 'font-mono text-xs')}>{valueOrDash(value)}</div>
    </div>
  );
}

function MiniStat({ label, value, tone = 'slate' }) {
  const toneClass = tone === 'red' ? 'text-red-700' : tone === 'orange' ? 'text-orange-700' : tone === 'amber' ? 'text-amber-700' : tone === 'emerald' ? 'text-emerald-700' : 'text-slate-900';
  return <div className="rounded-xl border border-slate-200 bg-slate-50 p-3"><p className="text-xs font-bold uppercase text-slate-400">{label}</p><p className={cx('mt-1 text-xl font-black', toneClass)}>{valueOrDash(value)}</p></div>;
}

function JsonBlock({ title, value, emptyText = 'No snapshot data stored for this section.' }) {
  const [copied, setCopied] = useState(false);
  const data = safeJson(value);
  const empty = isEmptyValue(data);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formatJson(data));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-950">
      <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3"><p className="text-sm font-bold text-white">{title}</p>{!empty && <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-bold text-white hover:bg-white/15"><Copy className="h-3.5 w-3.5" />{copied ? 'Copied' : 'Copy'}</button>}</div>
      {empty ? <div className="px-4 py-4 text-sm text-slate-400">{emptyText}</div> : <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words px-4 py-4 text-xs leading-5 text-slate-100">{formatJson(data)}</pre>}
    </div>
  );
}

function DecisionBadge({ decision }) {
  const tone = decision.tone === 'red' ? 'red' : decision.tone === 'amber' ? 'amber' : decision.tone === 'orange' ? 'orange' : 'slate';
  return <Badge tone={tone}>{decision.label}</Badge>;
}

function RiskAssessmentCard({ risk, fieldAccess }) {
  const classes = riskClasses(risk.level);
  return (
    <section className={cx('rounded-2xl border p-5 shadow-sm', classes.card)}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">{risk.level === 'critical' || risk.level === 'warning' ? <AlertTriangle className={cx('mt-0.5 h-5 w-5 shrink-0', classes.icon)} /> : <ShieldCheck className={cx('mt-0.5 h-5 w-5 shrink-0', classes.icon)} />}<div><p className="text-sm font-black">{risk.label}</p><p className="mt-1 text-sm">{risk.message}</p></div></div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:min-w-[420px]"><MiniStat label="Requested" value={unique(fieldAccess.requested_fields || []).length} /><MiniStat label="Returned" value={unique(fieldAccess.returned_fields || []).length} tone="red" /><MiniStat label="Masked" value={unique(fieldAccess.masked_fields || []).length} tone="amber" /><MiniStat label="Denied" value={unique(fieldAccess.denied_fields || []).length} /></div>
      </div>
    </section>
  );
}

function SnapshotStatusBanner({ snapshotAvailable, evidenceSource }) {
  if (snapshotAvailable) {
    return <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800 shadow-sm"><div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" /><div><p className="font-black">Immutable audit snapshot available</p><p className="mt-1 text-sm">This evidence is displayed from the audit database snapshot captured at event time. It does not depend on the original operational object still existing.</p><p className="mt-2 font-mono text-xs">Source: {evidenceSource}</p></div></div></section>;
  }
  return <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-800 shadow-sm"><div className="flex items-start gap-3"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" /><div><p className="font-black">Legacy audit event</p><p className="mt-1 text-sm">This event was created before Audit Schema v2. Only raw protobuf metadata is available. The UI will not fetch live PII, consent, document, or tenant objects to fill missing fields.</p><p className="mt-2 font-mono text-xs">Source: {evidenceSource || 'legacy_raw_proto'}</p></div></div></section>;
}

function FieldPills({ fields, tone = 'slate' }) {
  if (!Array.isArray(fields) || fields.length === 0) return <span className="text-sm text-slate-500">—</span>;
  return <div className="flex flex-wrap gap-2">{fields.map((field) => <Badge key={field} tone={tone}>{fieldLabel(field)}</Badge>)}</div>;
}

export default function AuditEvidenceDrawer({ eventId, event: fallbackEvent, onClose }) {
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(Boolean(eventId));
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedEventId, setCopiedEventId] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadEvidence() {
      if (!eventId) {
        setLoading(false);
        setError('Missing audit event id.');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const res = await auditAPI.getLogEvidence(eventId);
        if (!active) return;
        setEvidence(res.data || null);
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load audit evidence.');
      } finally {
        if (active) setLoading(false);
      }
    }

    loadEvidence();
    return () => { active = false; };
  }, [eventId]);

  const normalized = useMemo(() => {
    const data = evidence || {};
    const technical = data.technical || {};
    const eventData = data.event || fallbackEvent || technical || {};
    const auditSnapshot = data.audit_snapshot || {};
    const enrichedData = { ...data, event: eventData };
    return {
      event: eventData,
      actor: data.actor || {},
      subject: data.subject || {},
      consent: data.consent || {},
      document: data.document || {},
      document_access: data.document_access || {},
      processing: data.processing || {},
      governance: data.governance || {},
      technical,
      debug_audit_decode: data.debug_audit_decode || {},
      field_inventory: data.field_inventory || {},
      access_governance: buildAccessGovernance(enrichedData),
      field_access: buildFieldAccess(enrichedData),
      snapshot: data.snapshot || {},
      audit_snapshot: auditSnapshot,
      snapshot_available: isSnapshotAvailable(data, eventData),
      evidence_source: getSnapshotSource(data, eventData),
      raw: data,
    };
  }, [evidence, fallbackEvent]);

  const eventData = normalized.event || {};
  const technical = normalized.technical || {};
  const auditSnapshot = normalized.audit_snapshot || {};
  const actorSnapshot = auditSnapshot.actor_snapshot || {};
  const organizationSnapshot = auditSnapshot.organization_snapshot || {};
  const resourceSnapshot = auditSnapshot.resource_snapshot || {};
  const requestContext = auditSnapshot.request_context || {};
  const authorizationSnapshot = auditSnapshot.authorization_snapshot || {};
  const evidenceSnapshot = auditSnapshot.evidence_snapshot || {};
  const beforeSnapshot = auditSnapshot.before_snapshot || {};
  const afterSnapshot = auditSnapshot.after_snapshot || {};
  const changeSummary = auditSnapshot.change_summary || {};
  const outcomeSnapshot = auditSnapshot.outcome || {};
  const metadataSnapshot = auditSnapshot.metadata || {};

  const eventType = eventData.event_type || technical.event_type || fallbackEvent?.event_type || 'UNKNOWN_EVENT';
  const severity = eventData.severity || technical.severity || fallbackEvent?.severity || 'INFO';
  const eventTime = eventData.event_time || technical.event_time || fallbackEvent?.event_time || '';
  const resolvedEventId = eventData.event_id || technical.event_id || fallbackEvent?.event_id || eventId || '';
  const category = inferCategory(eventData);
  const outcome = getOutcome(eventData);
  const fieldAccess = normalized.field_access || {};
  const fieldInventory = normalized.field_inventory || {};
  const accessGovernance = normalized.access_governance || {};
  const risk = getEvidenceRisk({ fieldAccess, fieldInventory, eventData });

  const copyEventId = async () => {
    try {
      await navigator.clipboard.writeText(resolvedEventId);
      setCopiedEventId(true);
      setTimeout(() => setCopiedEventId(false), 1200);
    } catch {
      setCopiedEventId(false);
    }
  };

  const renderOverview = () => (
    <div className="space-y-5">
      <SnapshotStatusBanner snapshotAvailable={normalized.snapshot_available} evidenceSource={normalized.evidence_source} />
      <RiskAssessmentCard risk={risk} fieldAccess={fieldAccess} />
      <div className="grid gap-5 xl:grid-cols-2">
        <SectionCard title="Event summary" icon={Activity} description="High-level event identity and status.">
          <InfoRow label="Display event" value={getEventTitle(eventData)} />
          <InfoRow label="Raw event type" value={eventType} />
          <InfoRow label="Category" value={category} />
          <InfoRow label="Action" value={eventData.event_action || eventData.action} />
          <InfoRow label="Severity" value={severity} />
          <InfoRow label="Outcome" value={outcome} />
          <InfoRow label="Event time" value={formatDate(eventTime)} />
        </SectionCard>
        <SectionCard title="Primary parties" icon={User} description="Actor and resource involved in this event.">
          <InfoRow label="Actor" value={getActorLabel(eventData, actorSnapshot)} />
          <InfoRow label="Actor user ID" value={actorSnapshot.user_id || eventData.actor_user_id || eventData.admin_user_id} mono />
          <InfoRow label="Resource type" value={eventData.resource_type || resourceSnapshot.resource_type || category} />
          <InfoRow label="Resource" value={getResourceLabel(eventData, resourceSnapshot)} mono />
          <InfoRow label="Organization" value={organizationSnapshot.name || eventData.organization_name} />
          <InfoRow label="Schema" value={organizationSnapshot.schema_name || eventData.schema_name} />
        </SectionCard>
      </div>
    </div>
  );

  const renderActor = () => (
    <div className="grid gap-5 xl:grid-cols-2">
      <SectionCard title="Actor identity" icon={User} description="Email is shown only when the backend stores it for approved auth/account events.">
        <InfoRow label="Display name" value={actorSnapshot.display_name || normalized.actor.display_name || eventData.actor_display_name || eventData.admin_full_name} />
        <InfoRow label="User ID" value={actorSnapshot.user_id || normalized.actor.admin_user_id || eventData.actor_user_id || eventData.admin_user_id} mono />
        <InfoRow label="Role" value={actorSnapshot.role || normalized.actor.admin_role || eventData.admin_role} />
        {(actorSnapshot.email || normalized.actor.admin_email || eventData.admin_email) && <InfoRow label="Email — auth only" value={actorSnapshot.email || normalized.actor.admin_email || eventData.admin_email} />}
        <InfoRow label="IP address" value={requestContext.ip_address || normalized.actor.ip_address || eventData.ip_address} />
        <InfoRow label="User agent" value={requestContext.user_agent || normalized.actor.user_agent || eventData.user_agent} />
      </SectionCard>
      <JsonBlock title="Actor Snapshot" value={actorSnapshot} />
    </div>
  );

  const renderResource = () => (
    <div className="space-y-5">
      <SectionCard title="Resource" icon={Database} description="Resource metadata from the audit database snapshot.">
        <InfoRow label="Resource type" value={eventData.resource_type || resourceSnapshot.resource_type || category} />
        <InfoRow label="Resource ID" value={eventData.resource_id || resourceSnapshot.resource_id || eventData.pii_record_uuid || eventData.document_id} mono />
        <InfoRow label="Display name" value={eventData.resource_display_name || resourceSnapshot.display_name || resourceSnapshot.name} />
        <InfoRow label="PII record UUID" value={eventData.pii_record_uuid || resourceSnapshot.pii_record_uuid} mono />
        <InfoRow label="Document ID" value={eventData.document_id || resourceSnapshot.document_id} mono />
        <InfoRow label="Consent ID" value={eventData.consent_id || resourceSnapshot.consent_id} mono />
      </SectionCard>
      <JsonBlock title="Resource Snapshot" value={resourceSnapshot} />
      <JsonBlock title="Organization Snapshot" value={organizationSnapshot} />
    </div>
  );

  const renderEvidence = () => (
    <div className="space-y-5">
      <SectionCard title="Field access decision" icon={ShieldAlert} description="Exact field-level decision for governed PII view events.">
        {fieldAccess.available ? (
          <div className="grid gap-4 md:grid-cols-2">
            <FieldDecisionBox title="Requested fields" fields={fieldAccess.requested_fields} tone="slate" />
            <FieldDecisionBox title="Returned fields" fields={fieldAccess.returned_fields} tone="red" />
            <FieldDecisionBox title="Masked fields" fields={fieldAccess.masked_fields} tone="amber" />
            <FieldDecisionBox title="Denied fields" fields={fieldAccess.denied_fields} tone="slate" />
          </div>
        ) : <p className="text-sm text-slate-500">No field-level access decision was stored for this audit event.</p>}
      </SectionCard>

      <SectionCard title="Record field inventory" icon={Database} description="Known fields present in the record and whether this event accessed them.">
        {fieldInventory.available && Array.isArray(fieldInventory.fields) && fieldInventory.fields.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Field</th><th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Type</th><th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Sensitivity</th><th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Decision</th><th className="px-3 py-2 text-left text-xs font-bold uppercase text-slate-500">Source</th></tr></thead>
              <tbody className="divide-y divide-slate-100">{fieldInventory.fields.map((field) => { const fieldName = field.normalized_field_name || field.field_name; const decision = getFieldDecision(fieldAccess, fieldName); return <tr key={fieldName}><td className="px-3 py-2 font-mono text-xs">{field.field_name}</td><td className="px-3 py-2">{field.field_type || 'UNKNOWN'}</td><td className="px-3 py-2">{field.sensitivity || 'UNKNOWN'}</td><td className="px-3 py-2"><DecisionBadge decision={decision} /></td><td className="px-3 py-2">{field.source || '—'}</td></tr>; })}</tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-500">No field inventory was available for this event.</p>}
      </SectionCard>

      <JsonBlock title="Evidence Snapshot" value={evidenceSnapshot} />
    </div>
  );

  const renderAuthorization = () => (
    <div className="space-y-5">
      <SectionCard title="Access governance" icon={ShieldAlert} description="Reason, purpose, justification, and access mode captured at request time.">
        <InfoRow label="Access type" value={accessGovernance.access_type} />
        <InfoRow label="View mode" value={accessGovernance.view_mode || fieldAccess.view_mode} />
        <InfoRow label="Purpose code" value={accessGovernance.purpose_code || fieldAccess.purpose_code} />
        <InfoRow label="Access reason" value={accessGovernance.access_reason || fieldAccess.access_reason} />
        <InfoRow label="Justification" value={accessGovernance.justification || fieldAccess.justification} />
        {!(accessGovernance.view_mode || fieldAccess.view_mode || accessGovernance.purpose_code || fieldAccess.purpose_code || accessGovernance.access_reason || fieldAccess.access_reason || accessGovernance.justification || fieldAccess.justification) && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Governance fields were not present in this audit event. This must be fixed at the backend emitter for new PII/document view events; the drawer will display them once they exist in evidence_snapshot.
          </div>
        )}
      </SectionCard>
      <JsonBlock title="Authorization Snapshot" value={authorizationSnapshot} />
      <JsonBlock title="Policy Snapshot" value={fieldAccess.policy || accessGovernance.policy || {}} />
    </div>
  );

  const renderChange = () => (
    <div className="space-y-5">
      <div className="grid gap-5 xl:grid-cols-2"><JsonBlock title="Before Snapshot" value={beforeSnapshot} /><JsonBlock title="After Snapshot" value={afterSnapshot} /></div>
      <JsonBlock title="Change Summary" value={changeSummary} />
      <JsonBlock title="Outcome Snapshot" value={outcomeSnapshot} />
    </div>
  );

  const renderTechnical = () => (
    <div className="space-y-5">
      <SectionCard title="Technical evidence" icon={KeyRound} description="Audit pipeline and transport metadata.">
        <InfoRow label="Event ID" value={resolvedEventId} mono />
        <InfoRow label="Snapshot available" value={normalized.snapshot_available ? 'Yes' : 'No'} />
        <InfoRow label="Evidence source" value={normalized.evidence_source} />
        <InfoRow label="Live enrichment" value={normalized.debug_audit_decode?.live_enrichment === false ? 'No' : valueOrDash(normalized.debug_audit_decode?.live_enrichment)} />
        <InfoRow label="Endpoint" value={technical.endpoint_path || eventData.endpoint_path} />
        <InfoRow label="HTTP method" value={technical.http_method || eventData.http_method} />
        <InfoRow label="Kafka topic" value={technical.kafka_topic || eventData.kafka_topic} />
        <InfoRow label="Kafka partition" value={technical.kafka_partition || eventData.kafka_partition} />
        <InfoRow label="Kafka offset" value={technical.kafka_offset || eventData.kafka_offset} />
        <InfoRow label="Raw proto present" value={normalized.debug_audit_decode?.raw_proto_present === true || technical.raw_proto_available === true ? 'Yes' : 'No'} />
      </SectionCard>
      <JsonBlock title="Request Context" value={requestContext} />
      <JsonBlock title="Technical Metadata" value={metadataSnapshot} />
      <JsonBlock title="Debug Decode" value={normalized.debug_audit_decode} />
    </div>
  );

  const renderRaw = () => (
    <div className="space-y-5">
      <JsonBlock title="Complete Audit Snapshot" value={auditSnapshot} />
      <JsonBlock title="Complete Evidence Response" value={normalized.raw} />
    </div>
  );

  const tabRenderers = {
    overview: renderOverview,
    actor: renderActor,
    resource: renderResource,
    evidence: renderEvidence,
    authorization: renderAuthorization,
    change: renderChange,
    technical: renderTechnical,
    raw: renderRaw,
  };

  const ActiveTabIcon = TABS.find((tab) => tab.id === activeTab)?.icon || ChevronRight;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40 backdrop-blur-sm">
      <div className="h-full w-full max-w-6xl overflow-y-auto bg-slate-50 shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-black text-slate-950">Audit Evidence</h2><Badge tone="blue">{category}</Badge><SeverityBadge severity={severity} /><OutcomeBadge status={outcome} /></div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500"><span className="font-mono">{resolvedEventId || 'No event id'}</span><button type="button" onClick={copyEventId} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-bold text-slate-600 hover:bg-slate-100"><Copy className="h-3.5 w-3.5" />{copiedEventId ? 'Copied' : 'Copy ID'}</button>{eventTime && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDate(eventTime)} · {formatRelative(eventTime)}</span>}</div>
              <p className="mt-3 text-sm font-bold text-slate-700">{getEventTitle(eventData)}</p>
            </div>
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-slate-50"><X className="h-5 w-5" /></button>
          </div>

          <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const selected = activeTab === tab.id;
              return <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={cx('inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold ring-1 transition', selected ? 'bg-slate-950 text-white ring-slate-950' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50')}><Icon className="h-4 w-4" />{tab.label}</button>;
            })}
          </div>
        </div>

        <div className="space-y-5 p-6">
          {loading && <div className="rounded-2xl border border-slate-200 bg-white p-6"><div className="flex items-center gap-3 text-slate-600"><Loader2 className="h-5 w-5 animate-spin text-orange-600" />Loading audit evidence…</div></div>}
          {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700"><div className="flex items-start gap-3"><AlertTriangle className="mt-0.5 h-5 w-5" /><div><p className="font-bold">Could not load evidence</p><p className="mt-1 text-sm">{error}</p></div></div></div>}
          {!loading && !error && <><div className="flex items-center gap-2 text-sm font-bold text-slate-500"><ActiveTabIcon className="h-4 w-4" />{TABS.find((tab) => tab.id === activeTab)?.label}</div>{tabRenderers[activeTab]?.()}</>}
        </div>
      </div>
    </div>
  );
}

function FieldDecisionBox({ title, fields, tone }) {
  return <div className={cx('rounded-xl border p-4', tone === 'red' ? 'border-red-200 bg-red-50' : tone === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50')}><p className="text-xs font-bold uppercase text-slate-500">{title}</p><div className="mt-2"><FieldPills fields={fields} tone={tone} /></div></div>;
}
